'use client';

/**
 * Unified floating action bar anchored to the selection bbox.
 *
 * One bar across every selection mode (widget, annotation, connector,
 * multi-select). Hosts the SAME commands the right-click menu hosts —
 * pulled straight from the registry — so the user gets identical
 * coverage no matter which surface they use.
 *
 * Layout
 * --------
 * [ Duplicate ] | [ Align ▾ ] | [ ⋯ More ] | [ Delete ]
 *
 *   - The "More" button opens the full canvas context menu at the
 *     toolbar's anchor point, so any command not surfaced as an icon
 *     stays reachable without a right-click.
 *   - "Align" only appears when ≥2 positioned items are selected;
 *     opens a small popover with 6 align + 2 distribute icons.
 *
 * Positioning
 * -----------
 * The bar anchors to the TOP of the selection bbox in screen coords,
 * then translates through the React Flow viewport on every frame so
 * it tracks pan / zoom without jank. Flips BELOW the selection only
 * when the bbox is so close to the viewport top that the bar would
 * be clipped — Figma-style. This puts every selection toolbar
 * (per-kind AND universal) at the SAME relative position, so the
 * user always knows where to look.
 *
 * Coexistence with existing type-specific toolbars:
 *   - `AnnotationToolbar` (sticky / text / shape) and
 *     `ConnectorEdgeToolbar` already own the top slot for their
 *     single-item selection. To avoid stacking two top toolbars on
 *     one widget, this bar SUPPRESSES itself when the selection is
 *     a single annotation or single connector — the per-kind bar
 *     covers the same actions (delete + property edits) for that
 *     case. Right-click + keyboard shortcuts still reach every
 *     command from the registry, so no functionality is hidden.
 *   - For everything else (any non-annotation / non-connector widget,
 *     OR a multi-selection of any kind), this is the only toolbar
 *     that renders — sits at the top of the selection bbox.
 */

import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import { useStore as useRfStore } from '@xyflow/react';
import { useT } from '@/components/i18n-provider';
import { cn } from '@/lib/utils';
import { haptic } from '@/lib/haptic';
import {
  buildWorkspaceCommands,
  filterToolbarCommands,
  formatShortcut,
  selectionBBox,
  type CommandContext,
  type WorkspaceCommand,
  type WorkspaceClipboard,
} from '@/lib/workspace/commands';
import { CommandIcon } from './command-icons';
import { useCommandContext } from './use-command-context';
import { useWorkspaceSelection } from './selection-context';
import { useWorkspaceItems } from './store';
import {
  buildMenuItemsFromCommands,
  type CanvasMenuItem,
} from './canvas-context-menu';

const ALIGN_COMMANDS = [
  'align-left',
  'align-center-h',
  'align-right',
  'align-top',
  'align-center-v',
  'align-bottom',
] as const;

const DISTRIBUTE_COMMANDS = ['distribute-h', 'distribute-v'] as const;

const TOOLBAR_HEIGHT = 36;
const TOOLBAR_GAP = 12;

interface SelectionFloatingToolbarProps {
  /** Clipboard facade — forwarded into the command context so the
   *  "More" menu can offer Cut / Copy / Paste rows. */
  clipboard: WorkspaceClipboard | null;
  /** Open the unified context menu at a viewport coord. The toolbar
   *  delegates the "More" button to the menu so we don't duplicate
   *  every action on two surfaces. */
  onOpenMoreMenu: (
    anchor: { x: number; y: number },
    items: CanvasMenuItem[],
  ) => void;
}

function SelectionFloatingToolbarImpl({
  clipboard,
  onOpenMoreMenu,
}: SelectionFloatingToolbarProps) {
  const t = useT();
  const selection = useWorkspaceSelection();
  const items = useWorkspaceItems();
  const commands = useMemo(() => buildWorkspaceCommands(), []);
  const buildCtx = useCommandContext(clipboard);

  // Live RF viewport — re-render whenever the user pans/zooms so
  // the toolbar tracks the selection bbox.
  const tx = useRfStore((s) => s.transform[0]);
  const ty = useRfStore((s) => s.transform[1]);
  const tz = useRfStore((s) => s.transform[2]);

  const [alignOpen, setAlignOpen] = useState(false);
  const alignButtonRef = useRef<HTMLButtonElement | null>(null);

  // Suppress this universal bar whenever a per-kind toolbar already
  // owns the top slot for the single-item selection (annotation
  // sticky / text / shape, OR a connector). Stacking two top
  // toolbars on the same widget was the "two top + bottom menus"
  // confusion the user flagged — keep ONE bar visible per gesture,
  // with the per-kind editor winning when it applies. Multi-select
  // and any other widget type fall through to render this bar.
  const suppressed = useMemo(() => {
    if (selection.nodeIds.size !== 1 || selection.edgeIds.size !== 0) {
      // Connector single-select: per-edge toolbar wins.
      if (selection.nodeIds.size === 0 && selection.edgeIds.size === 1) {
        return true;
      }
      return false;
    }
    const onlyId = [...selection.nodeIds][0];
    const it = items.find((x) => x.id === onlyId);
    if (!it) return false;
    // Link-kind annotations explicitly DON'T render
    // AnnotationToolbar (see `annotation-node.tsx`), so the
    // floating bar is the only universal-action surface they
    // have. Keep it visible for those.
    if (it.type === 'annotation') {
      const kind = (it.state.cache?.kind as string | undefined) ?? 'sticky';
      return kind !== 'link';
    }
    // Windowed widgets — agent nodes and chrome'd content (html /
    // iframe / text) widgets — already carry their own NodeFrame header
    // (action dots + window controls), and the right-click context menu
    // + ⌘D / Delete shortcuts cover Duplicate / Delete. The floating
    // [Duplicate | Align | More | Delete] bar is redundant clutter on
    // them, so suppress it. Headerless media (pasted image / video,
    // `hideHeader`) have NO chrome, so they KEEP the bar as their only
    // visible duplicate/delete affordance.
    if (it.type === 'agent') return true;
    if (it.type === 'content' && !it.state.hideHeader) return true;
    return false;
  }, [items, selection.edgeIds.size, selection.nodeIds]);

  const ctx = useMemo<CommandContext | null>(() => {
    if (selection.nodeIds.size === 0 && selection.edgeIds.size === 0) {
      return null;
    }
    return buildCtx();
    // We want fresh ctx on every items / selection change so
    // command predicates evaluate against live data.
  }, [buildCtx, items, selection.edgeIds, selection.nodeIds]);

  const bbox = useMemo(() => (ctx ? selectionBBox(ctx) : null), [ctx]);

  // Position the bar ABOVE the bbox in screen coords (Figma /
  // Confluence convention — toolbar tucks just above the selection
  // ring). Flips BELOW only when the selection is close enough to
  // the viewport top that an above-anchor would clip out of frame.
  // Centred horizontally.
  const screenPos = useMemo(() => {
    if (!bbox) return null;
    const cxFlow = bbox.x + bbox.width / 2;
    const bottomFlow = bbox.y + bbox.height;
    const topFlow = bbox.y;
    const cxScreen = cxFlow * tz + tx;
    const bottomScreen = bottomFlow * tz + ty;
    const topScreen = topFlow * tz + ty;
    const vh = typeof window === 'undefined' ? 1024 : window.innerHeight;
    const wantAbove = topScreen - TOOLBAR_GAP - TOOLBAR_HEIGHT >= 16;
    const y = wantAbove
      ? topScreen - TOOLBAR_GAP - TOOLBAR_HEIGHT
      : Math.min(vh - 16 - TOOLBAR_HEIGHT, bottomScreen + TOOLBAR_GAP);
    return { x: cxScreen, y };
  }, [bbox, tx, ty, tz]);

  const toolbarCmds = useMemo<WorkspaceCommand[]>(() => {
    if (!ctx) return [];
    return filterToolbarCommands(commands, ctx);
  }, [commands, ctx]);

  const moreItems = useMemo<CanvasMenuItem[]>(() => {
    if (!ctx) return [];
    return buildMenuItemsFromCommands(commands, ctx, 'selection');
  }, [commands, ctx]);

  const showAlign = useMemo(() => {
    if (!ctx) return false;
    const aligns = commands.filter((c) =>
      ALIGN_COMMANDS.includes(c.id as (typeof ALIGN_COMMANDS)[number]),
    );
    return aligns.some((c) => c.predicate(ctx));
  }, [commands, ctx]);

  // Dismiss the align popover on outside click / Esc.
  useEffect(() => {
    if (!alignOpen) return;
    const onPointer = (e: PointerEvent) => {
      const btn = alignButtonRef.current;
      if (!btn) return;
      const wrap = btn.closest('[data-selection-toolbar]') as HTMLElement | null;
      if (wrap && e.target instanceof Node && wrap.contains(e.target)) return;
      setAlignOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAlignOpen(false);
    };
    document.addEventListener('pointerdown', onPointer, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [alignOpen]);

  const onOpenMore = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      onOpenMoreMenu({ x: rect.left, y: rect.bottom + 4 }, moreItems);
    },
    [moreItems, onOpenMoreMenu],
  );

  if (!ctx || !bbox || !screenPos) return null;
  if (suppressed) return null;

  // Skip cmds we render natively (delete + align live in dedicated
  // slots) so the icon row doesn't duplicate them.
  const NATIVE_RENDERED = new Set<string>([
    'delete',
    'align-left',
    'align-center-h',
    'align-right',
    'align-top',
    'align-center-v',
    'align-bottom',
    'distribute-h',
    'distribute-v',
  ]);
  const iconRow = toolbarCmds.filter((c) => !NATIVE_RENDERED.has(c.id));
  const deleteCmd = toolbarCmds.find((c) => c.id === 'delete');

  const style: CSSProperties = {
    position: 'absolute',
    left: 0,
    top: 0,
    transform: `translate(calc(${screenPos.x}px - 50%), ${screenPos.y}px)`,
    zIndex: 30,
    // Above selected nodes (1001) and the alignment guides overlay
    // (1001) so the bar always wins the click. RF puts selected
    // nodes at 1001 via CSS, which would otherwise occlude the bar
    // when the selection is large.
    isolation: 'isolate',
  };

  return (
    <div
      data-selection-toolbar
      role="toolbar"
      aria-label={t('workspace.toolbar.selectionActions')}
      className="pointer-events-none"
      style={style}
    >
      <div
        className={cn(
          'pointer-events-auto inline-flex items-center gap-0.5',
          'rounded-xl border border-border/70 bg-popover/95 backdrop-blur-md',
          'shadow-lg px-1 py-1 text-foreground',
        )}
        // Stop the canvas's pane-click / pick-through listeners from
        // interpreting bar clicks as canvas gestures.
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {iconRow.map((c) => (
          <ToolButton
            key={c.id}
            label={`${c.label}${formatShortcut(c.shortcut, ctx.platform) ? ` (${formatShortcut(c.shortcut, ctx.platform)})` : ''}`}
            onClick={() => {
              haptic('light');
              void c.run(ctx);
            }}
            tone={c.tone}
          >
            <CommandIcon id={c.icon} className="h-3.5 w-3.5" />
          </ToolButton>
        ))}

        {showAlign && (
          <>
            <Divider />
            <div className="relative">
              <ToolButton
                buttonRef={alignButtonRef}
                label={t('workspace.toolbar.alignDistribute')}
                onClick={() => setAlignOpen((v) => !v)}
                active={alignOpen}
              >
                <CommandIcon id="align-center-h" className="h-3.5 w-3.5" />
              </ToolButton>
              {alignOpen && <AlignPopover ctx={ctx} commands={commands} />}
            </div>
          </>
        )}

        <Divider />
        <ToolButton
          label={t('workspace.toolbar.moreActions')}
          onClick={onOpenMore as unknown as () => void}
          onClickEvent={onOpenMore}
        >
          <CommandIcon id="more" className="h-3.5 w-3.5" />
        </ToolButton>

        {deleteCmd && (
          <>
            <Divider />
            <ToolButton
              label={t('workspace.toolbar.deleteSelection')}
              onClick={() => {
                haptic('medium');
                void deleteCmd.run(ctx);
              }}
              tone="danger"
            >
              <CommandIcon id="delete" className="h-3.5 w-3.5" />
            </ToolButton>
          </>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------------

function ToolButton({
  children,
  onClick,
  onClickEvent,
  label,
  active,
  tone,
  buttonRef,
}: {
  children: React.ReactNode;
  onClick?: () => void;
  onClickEvent?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  label: string;
  active?: boolean;
  tone?: 'default' | 'danger';
  buttonRef?: React.Ref<HTMLButtonElement>;
}) {
  return (
    <button
      ref={buttonRef}
      type="button"
      aria-label={label}
      aria-pressed={active}
      title={label}
      onClick={(e) => {
        e.stopPropagation();
        if (onClickEvent) onClickEvent(e);
        else if (onClick) onClick();
      }}
      className={cn(
        'inline-flex h-7 items-center justify-center gap-1 rounded-md px-2 transition-colors',
        'text-foreground/85 hover:bg-foreground/8 active:bg-foreground/12',
        active && 'bg-primary/12 text-primary',
        tone === 'danger' &&
          'hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400',
      )}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span aria-hidden className="mx-0.5 h-5 w-px bg-border/80" />;
}

function AlignPopover({
  ctx,
  commands,
}: {
  ctx: CommandContext;
  commands: WorkspaceCommand[];
}) {
  const aligns = ALIGN_COMMANDS.map((id) =>
    commands.find((c) => c.id === id),
  ).filter((c): c is WorkspaceCommand => !!c && c.predicate(ctx));
  const distributes = DISTRIBUTE_COMMANDS.map((id) =>
    commands.find((c) => c.id === id),
  ).filter((c): c is WorkspaceCommand => !!c && c.predicate(ctx));

  return (
    <div
      role="menu"
      className={cn(
        'absolute top-[calc(100%+8px)] left-1/2 -translate-x-1/2',
        'glass-panel rounded-lg p-2 shadow-2xl',
        'min-w-[200px]',
        'z-[210]',
      )}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Align
      </div>
      <div className="grid grid-cols-3 gap-1">
        {aligns.map((c) => (
          <button
            key={c.id}
            type="button"
            title={c.label}
            aria-label={c.label}
            onClick={() => {
              haptic('light');
              void c.run(ctx);
            }}
            className={cn(
              'inline-flex h-7 items-center justify-center rounded-md',
              'text-foreground/85 hover:bg-foreground/8 active:bg-foreground/12',
            )}
          >
            <CommandIcon id={c.icon} className="h-4 w-4" />
          </button>
        ))}
      </div>
      {distributes.length > 0 && (
        <>
          <div className="mt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Distribute
          </div>
          <div className="grid grid-cols-2 gap-1">
            {distributes.map((c) => (
              <button
                key={c.id}
                type="button"
                title={c.label}
                aria-label={c.label}
                onClick={() => {
                  haptic('light');
                  void c.run(ctx);
                }}
                className={cn(
                  'inline-flex h-7 items-center justify-center rounded-md',
                  'text-foreground/85 hover:bg-foreground/8 active:bg-foreground/12',
                )}
              >
                <CommandIcon id={c.icon} className="h-4 w-4" />
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export const SelectionFloatingToolbar = memo(SelectionFloatingToolbarImpl);
// Back-compat default export name used by `workspace-canvas.tsx` —
// keeps the existing import path working while the canvas migrates
// to the new name.
export const SelectionToolbar = SelectionFloatingToolbar;
